import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  Shield,
  BarChart3,
  Users,
  Smartphone,
  Key,
  FileCheck,
  Workflow,
  ArrowRight,
  Clock,
  X,
} from "lucide-react";
import logoSvg from "/favicon.svg";
import { Wordmark } from "@/components/ui/wordmark";
import { useLocation } from "wouter";
import {
  validateEmail,
  validateRequired,
  validatePassword,
  formatUserFriendlyError,
} from "@/lib/errorHandling";
import {
  useGoogleReCaptcha,
  GoogleReCaptchaProvider,
} from "react-google-recaptcha-v3";

function AuthSimple() {
  const [, setLocation] = useLocation();
  const [authStep, setAuthStep] = useState<"email" | "login" | "register">(
    "email",
  );
  const [userEmail, setUserEmail] = useState("");
  const [authMethods, setAuthMethods] = useState<string[]>([]);
  const [showMfaVerification, setShowMfaVerification] = useState(false);
  const [mfaEmail, setMfaEmail] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [showBackupCodeInput, setShowBackupCodeInput] = useState(false);
  const [showTimeoutMessage, setShowTimeoutMessage] = useState(false);
  const { toast } = useToast();
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Add auth-page class to body to hide support widget on login page
  useEffect(() => {
    document.body.classList.add("auth-page");
    return () => {
      document.body.classList.remove("auth-page");
    };
  }, []);

  // Check for session timeout parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("timeout") === "true") {
      // Show a persistent, dismissible banner (instead of an auto-disappearing
      // toast) so the user clearly understands why they were returned to login.
      setShowTimeoutMessage(true);
      // Clean up the URL parameter
      window.history.replaceState({}, "", window.location.pathname);
    }

    // Handle SSO error redirects
    const ssoError = urlParams.get("error");
    if (ssoError) {
      const errorMessages: Record<
        string,
        { title: string; description: string }
      > = {
        account_not_found: {
          title: "Account Not Found",
          description:
            "No TechAlign account is associated with this Microsoft account. Please sign in with your email or contact your administrator.",
        },
        sso_not_configured: {
          title: "SSO Not Available",
          description:
            "Microsoft SSO is not configured. Please sign in with your email and password.",
        },
        organization_inactive: {
          title: "Account Suspended",
          description:
            "Your organization's subscription has expired. Please contact support@techalign.app to reactivate.",
        },
        login_disabled: {
          title: "Login Disabled",
          description:
            "Your login has been disabled. Please contact your administrator.",
        },
        oauth_error: {
          title: "Sign In Failed",
          description:
            "Microsoft authentication was cancelled or failed. Please try again.",
        },
        org_mismatch: {
          title: "Sign In Failed",
          description:
            "There was a security error during sign in. Please try again.",
        },
      };

      const msg = errorMessages[ssoError] || {
        title: "Sign In Failed",
        description:
          "An error occurred during Microsoft sign in. Please try again.",
      };

      toast({ ...msg, variant: "destructive" });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [toast]);

  // Simple form state
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  const [registerData, setRegisterData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    organizationName: "",
  });

  const [emailInput, setEmailInput] = useState("");

  // Refs for password manager autofill detection
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Autofill detection for password managers
  useEffect(() => {
    let interval: NodeJS.Timeout;
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds at 100ms intervals

    const checkAutofill = () => {
      let updated = false;

      // Check email field
      if (emailRef.current && emailRef.current.value !== loginData.email) {
        setLoginData((prev) => ({ ...prev, email: emailRef.current!.value }));
        updated = true;
      }

      // Check password field
      if (
        passwordRef.current &&
        passwordRef.current.value !== loginData.password
      ) {
        setLoginData((prev) => ({
          ...prev,
          password: passwordRef.current!.value,
        }));
        updated = true;
      }

      attempts++;

      // Stop polling if both fields are synced or max attempts reached
      if (updated || attempts >= maxAttempts) {
        clearInterval(interval);
      }
    };

    // Start polling after a short delay to allow password managers to fill
    const timeoutId = setTimeout(() => {
      interval = setInterval(checkAutofill, 100);
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      if (interval) clearInterval(interval);
    };
  }, [loginData.email, loginData.password]);

  // Check auth status and Entra availability
  const { data: authStatus } = useQuery<any>({
    queryKey: ["/api/auth/status"],
    retry: false,
  });

  // Check if platform-level Microsoft SSO is available (for the one-click button)
  const { data: ssoAvailable } = useQuery<any>({
    queryKey: ["/api/auth/sso-available"],
    retry: false,
  });

  const loginMutation = useMutation({
    mutationFn: async (data: typeof loginData & { captchaToken: string }) => {
      // Get CSRF token first
      const csrfResponse = await fetch("/api/csrf-token", {
        credentials: "include",
      });
      const { csrfToken } = await csrfResponse.json();

      // Make login request with CSRF token and captcha token
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw { statusCode: response.status, appError: errorData };
      }

      return response.json();
    },
    onSuccess: (data) => {
      if (data.requiresMfa) {
        // Show MFA verification dialog
        setMfaEmail(data.email);
        setShowMfaVerification(true);
        toast({
          title: "MFA Required",
          description: "Please enter your verification code to complete login.",
        });
      } else if (data.requiresMfaSetup) {
        // User needs to set up MFA (e.g., organization owner)
        // Invalidate queries first to ensure fresh auth state
        queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
        queryClient.invalidateQueries({ queryKey: ["/api/auth/status"] });

        toast({
          title: "MFA Setup Required",
          description: "Redirecting to security settings...",
          variant: "default",
        });

        // Redirect immediately to profile with MFA setup flag
        setTimeout(() => {
          setLocation("/profile?setupMfa=true");
        }, 500);
      } else {
        // Complete login success
        queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
        queryClient.invalidateQueries({ queryKey: ["/api/auth/status"] });

        toast({
          title: "Login Successful",
          description: "Welcome back!",
        });

        setTimeout(() => {
          setLocation("/");
        }, 100);
      }
    },
    onError: (error: any) => {
      // Check for EMAIL_NOT_VERIFIED specifically from server response
      if (
        error.statusCode === 403 &&
        error.appError?.code === "EMAIL_NOT_VERIFIED"
      ) {
        // Extract verification info from the error response
        const userId = error.appError?.userId;
        const userEmail = error.appError?.email || loginData.email;

        // Store verification info for the verification page
        if (userId && userEmail) {
          const verificationInfo = {
            userId: userId,
            email: userEmail,
            organizationName: undefined,
          };
          sessionStorage.setItem(
            "verificationInfo",
            JSON.stringify(verificationInfo),
          );
        }

        toast({
          title: "Email Verification Required",
          description:
            "Please verify your email address before signing in. Redirecting to verification page...",
          variant: "destructive",
        });

        setTimeout(() => {
          if (userId) {
            setLocation(
              `/verify-email/${userId}?email=${encodeURIComponent(userEmail)}`,
            );
          } else {
            // Fallback if we don't have userId
            setLocation(`/verify-email?email=${encodeURIComponent(userEmail)}`);
          }
        }, 1500);

        return;
      }

      // Also check for the raw error field (fallback)
      if (
        error.statusCode === 403 &&
        error.appError?.error === "EMAIL_NOT_VERIFIED"
      ) {
        // Extract verification info from the error response
        const userId = error.appError?.userId;
        const userEmail = error.appError?.email || loginData.email;

        // Store verification info for the verification page
        if (userId && userEmail) {
          const verificationInfo = {
            userId: userId,
            email: userEmail,
            organizationName: undefined,
          };
          sessionStorage.setItem(
            "verificationInfo",
            JSON.stringify(verificationInfo),
          );
        }

        toast({
          title: "Email Verification Required",
          description:
            "Please verify your email address before signing in. Redirecting to verification page...",
          variant: "destructive",
        });

        setTimeout(() => {
          if (userId) {
            setLocation(
              `/verify-email/${userId}?email=${encodeURIComponent(userEmail)}`,
            );
          } else {
            // Fallback if we don't have userId
            setLocation(`/verify-email?email=${encodeURIComponent(userEmail)}`);
          }
        }, 1500);

        return;
      }

      // Check for organization inactive error
      if (
        error.statusCode === 403 &&
        error.appError?.error === "ORGANIZATION_INACTIVE"
      ) {
        toast({
          title: "Account Suspended",
          description:
            "Your organization's subscription has expired. Please contact support@techalign.app to reactivate your account.",
          variant: "destructive",
          duration: 10000, // Show for 10 seconds
        });
        return;
      }

      // Check for login disabled error
      if (
        error.statusCode === 403 &&
        error.appError?.error === "LOGIN_DISABLED"
      ) {
        toast({
          title: "Login Disabled",
          description:
            "Your login has been disabled. Please contact your administrator for assistance.",
          variant: "destructive",
          duration: 10000, // Show for 10 seconds
        });
        return;
      }

      const message = formatUserFriendlyError(error);
      toast({
        title: "Login Failed",
        description: message,
        variant: "destructive",
      });
    },
  });

  const mfaVerifyMutation = useMutation({
    mutationFn: async (data: {
      email: string;
      token: string;
      isBackupCode?: boolean;
    }) => {
      // Get CSRF token first
      const csrfResponse = await fetch("/api/csrf-token", {
        credentials: "include",
      });
      const { csrfToken } = await csrfResponse.json();

      // Make MFA verification request with CSRF token
      const response = await fetch("/api/auth/verify-mfa", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw { statusCode: response.status, appError: errorData };
      }

      return response.json();
    },
    onSuccess: () => {
      // Complete login success
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/status"] });

      setShowMfaVerification(false);
      setMfaToken("");
      setMfaEmail("");

      toast({
        title: "Login Successful",
        description: "Welcome back!",
      });

      setTimeout(() => {
        setLocation("/");
      }, 100);
    },
    onError: (error: any) => {
      // Handle specific MFA error codes
      let title = "MFA Verification Failed";
      let description = "Please check your verification code and try again.";

      if (error?.code === "SESSION_EXPIRED") {
        title = "Session Expired";
        description = "Your login session has expired. Please log in again.";
        // Reset MFA dialog and redirect to login
        setShowMfaVerification(false);
        setMfaToken("");
        setMfaEmail("");
        setBackupCode("");
      } else if (error?.code === "INVALID_CODE") {
        description =
          "The verification code is incorrect. Please check your authenticator app and try again.";
      } else if (error?.code === "LOGIN_COMPLETION_FAILED") {
        description =
          "Login failed after verification. Please try logging in again.";
        setShowMfaVerification(false);
      } else {
        const message = formatUserFriendlyError(error);
        description = message;
      }

      toast({
        title,
        description,
        variant: "destructive",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (data: typeof registerData) => {
      const { confirmPassword, ...registerSubmitData } = data;

      // Get CSRF token first
      const csrfResponse = await fetch("/api/csrf-token", {
        credentials: "include",
      });
      const { csrfToken } = await csrfResponse.json();

      // Make registration request with CSRF token
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify(registerSubmitData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw { statusCode: response.status, appError: errorData };
      }

      return response.json();
    },
    onSuccess: (data) => {
      // Registration successful - redirect to email verification
      const verificationInfo = {
        userId: data.userId,
        email: data.email,
        organizationName: data.organizationName,
      };

      // Store verification info in session storage for the verification page
      sessionStorage.setItem(
        "verificationInfo",
        JSON.stringify(verificationInfo),
      );

      toast({
        title: "Registration Successful",
        description: "Please check your email for a verification code.",
      });

      // Show development verification code if available
      if (data.verificationCode && import.meta.env.DEV) {
        toast({
          title: "Development Mode",
          description: `Verification code: ${data.verificationCode}`,
          duration: 10000,
        });
      }

      // Navigate to email verification page
      setTimeout(() => {
        setLocation(
          `/verify-email/${data.userId}?email=${encodeURIComponent(data.email)}&organizationName=${encodeURIComponent(data.organizationName || "")}`,
        );
      }, 100);
    },
    onError: (error) => {
      const message = formatUserFriendlyError(error);
      toast({
        title: "Registration Failed",
        description: message,
        variant: "destructive",
      });
    },
  });

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Enhanced validation with specific error messages
    const emailError =
      validateRequired(loginData.email, "email") ||
      validateEmail(loginData.email);
    const passwordError = validateRequired(loginData.password, "password");

    if (emailError) {
      toast({
        title: "Validation Error",
        description:
          emailError.message +
          (emailError.resolution ? `\n\n${emailError.resolution}` : ""),
        variant: "destructive",
      });
      return;
    }

    if (passwordError) {
      toast({
        title: "Validation Error",
        description:
          passwordError.message +
          (passwordError.resolution ? `\n\n${passwordError.resolution}` : ""),
        variant: "destructive",
      });
      return;
    }

    if (!executeRecaptcha) {
      toast({
        title: "Verification Error",
        description: "reCAPTCHA is not ready. Please refresh the page.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Execute reCAPTCHA v3 to get token
      const captchaToken = await executeRecaptcha("login");
      loginMutation.mutate({ ...loginData, captchaToken });
    } catch (error) {
      console.error("reCAPTCHA error:", error);
      toast({
        title: "Verification Failed",
        description: "Failed to verify you're not a robot. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRegisterSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Enhanced validation with specific error messages and guidance
    const firstNameError = validateRequired(
      registerData.firstName,
      "first name",
    );
    const lastNameError = validateRequired(registerData.lastName, "last name");
    const emailError =
      validateRequired(registerData.email, "email") ||
      validateEmail(registerData.email);
    const passwordError =
      validateRequired(registerData.password, "password") ||
      validatePassword(registerData.password);
    const confirmPasswordError = validateRequired(
      registerData.confirmPassword,
      "confirm password",
    );
    const organizationNameError = validateRequired(
      registerData.organizationName,
      "organization name",
    );

    // Check for any validation errors
    const errors = [
      firstNameError,
      lastNameError,
      emailError,
      passwordError,
      confirmPasswordError,
      organizationNameError,
    ].filter(Boolean);

    if (errors.length > 0) {
      const firstError = errors[0]!;
      toast({
        title: "Validation Error",
        description:
          firstError.message +
          (firstError.resolution ? `\n\n${firstError.resolution}` : ""),
        variant: "destructive",
      });
      return;
    }

    // Check if passwords match
    if (registerData.password !== registerData.confirmPassword) {
      toast({
        title: "Validation Error",
        description:
          "Passwords do not match.\n\nPlease ensure both password fields contain the same value.",
        variant: "destructive",
      });
      return;
    }

    registerMutation.mutate(registerData);
  };

  const checkAuthMethodsMutation = useMutation({
    mutationFn: async (email: string) => {
      // Get CSRF token first
      const csrfResponse = await fetch("/api/csrf-token", {
        credentials: "include",
      });
      const { csrfToken } = await csrfResponse.json();

      const response = await fetch("/api/auth/check-methods", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw errorData;
      }

      return response.json();
    },
    onSuccess: (data) => {
      if (!data.canRegister) {
        // User exists — proceed to login
        setUserEmail(emailInput);
        setAuthMethods(data.authMethods);

        // Update login form email
        setLoginData((prev) => ({ ...prev, email: emailInput }));

        if (data.authMethods.includes("sso")) {
          // SSO user — redirect straight to Microsoft instead of showing intermediate step
          window.location.href = `/api/auth/azure?email=${encodeURIComponent(emailInput)}`;
        } else if (data.authMethods.includes("local")) {
          setAuthStep("login");
        }
      } else {
        // Account doesn't exist - show error toast
        toast({
          title: "Account Not Found",
          description:
            'This email address is not associated with an account. Please try typing it again or click the "Don\'t have an account?" button below to get started.',
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      const message = error.error || "Failed to check authentication methods";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!emailInput.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    checkAuthMethodsMutation.mutate(emailInput);
  };

  const handleBackToEmail = () => {
    setAuthStep("email");
    setUserEmail("");
    setAuthMethods([]);
    setEmailInput("");
    setLoginData({ email: "", password: "" });
    setRegisterData({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      organizationName: "",
    });
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-[58%] relative bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 p-12 flex-col overflow-hidden">
        {/* Background orbs */}
        <div className="absolute top-[-20%] right-[-10%] w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[400px] h-[400px] bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        {/* Grid texture */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />
        {/* Logo */}
        <div className="relative z-10">
          <div className="flex items-center gap-4">
            <img
              src={logoSvg}
              alt="TechAlign"
              className="h-16 w-16 object-contain bg-white/90 rounded-xl p-1.5"
            />
            <Wordmark tone="on-dark" className="text-3xl" />
          </div>
        </div>
        {/* Hero text + features */}
        <div className="flex-1 flex items-center">
          <div className="relative z-10 max-w-lg">
            <h2 className="text-4xl font-bold text-white mb-4 leading-tight tracking-tight">
              Technical alignment,
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
                simplified.
              </span>
            </h2>
            <p className="text-lg text-slate-400 mb-10 leading-relaxed">
              Streamline assessments, automate Business Reviews, and deliver
              actionable insights for your customers.
            </p>
            <div className="space-y-4">
              {[
                { icon: FileCheck, text: "Comprehensive Assessment Templates" },
                {
                  icon: BarChart3,
                  text: "AI-Powered Business Review Generation",
                },
                { icon: Workflow, text: "Role-Based Workflow Management" },
                { icon: Users, text: "Multiple Integration" },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3 group">
                  <div className="w-8 h-8 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center group-hover:bg-blue-500/10 group-hover:border-blue-500/20 transition-all">
                    <Icon className="h-4 w-4 text-blue-400" />
                  </div>
                  <span className="text-sm text-slate-300 font-medium">
                    {text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Auth Forms */}
      <div className="w-full lg:w-[42%] flex items-center justify-center px-6 lg:px-12 relative bg-white min-h-screen">
        {/* Gradient accent top bar */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600" />

        <div className="w-full max-w-sm" style={{ overflow: "visible" }}>
          {/* Session timeout notice — dismissible */}
          {showTimeoutMessage && (
            <div
              role="alert"
              className="mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900"
              data-testid="banner-session-timeout"
            >
              <Clock className="h-5 w-5 flex-shrink-0 text-amber-600 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">Session expired</p>
                <p className="text-sm text-amber-800">
                  You were signed out due to inactivity. Please sign in again to
                  continue.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowTimeoutMessage(false)}
                aria-label="Dismiss"
                className="flex-shrink-0 rounded-md p-1 text-amber-600 hover:bg-amber-100 hover:text-amber-900 transition-colors"
                data-testid="button-dismiss-timeout"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Logo for mobile */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img
              src={logoSvg}
              alt="TechAlign"
              className="h-10 w-10 object-contain"
            />
            <Wordmark tone="on-light" className="text-xl" />
          </div>

          {/* Step header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-1">
              {authStep === "email" && "Welcome back"}
              {authStep === "login" && "Welcome back"}
              {authStep === "register" && "Create your account"}
            </h2>
            <p className="text-slate-500">
              {authStep === "email" && "Enter your email to get started"}
              {authStep === "login" && (
                <span>
                  Signing in as{" "}
                  <span className="font-medium text-slate-700">
                    {userEmail}
                  </span>
                </span>
              )}
              {authStep === "register" && "Start your 14-day free trial"}
            </p>
          </div>

          <div className="space-y-3" style={{ overflow: "visible" }}>
            {/* Step 1: Email Input */}
            {authStep === "email" && (
              <>
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-slate-700 mb-1.5"
                    >
                      Email Address
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="name@company.com"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      autoComplete="email"
                      required
                      className="w-full h-12 px-4 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all shadow-sm"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full h-12 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-medium rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-sm"
                    disabled={checkAuthMethodsMutation.isPending}
                  >
                    {checkAuthMethodsMutation.isPending ? (
                      "Checking..."
                    ) : (
                      <>
                        <span>Continue</span>
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </form>

                {/* Microsoft SSO button — only shown when platform-level SSO is configured */}
                {ssoAvailable?.available && (
                  <>
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-slate-200" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-3 text-slate-400">or</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        window.location.href = "/api/auth/azure/universal";
                      }}
                      className="w-full h-12 bg-white border border-slate-200 rounded-xl text-slate-700 font-medium flex items-center justify-center gap-3 hover:bg-slate-50 transition-all shadow-sm"
                    >
                      <img
                        src="/microsoft-logo.png"
                        alt="Microsoft"
                        className="h-5 w-5 object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                      Sign in with Microsoft
                    </button>
                  </>
                )}
              </>
            )}

            {/* Step 2: Local Login Form */}
            {authStep === "login" && (
              <form
                onSubmit={handleLoginSubmit}
                action="/login"
                method="post"
                autoComplete="on"
                className="space-y-4"
              >
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-slate-700 mb-1.5"
                  >
                    Password
                  </label>
                  <input
                    ref={passwordRef}
                    type="password"
                    id="password"
                    name="password"
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={loginData.password}
                    onChange={(e) =>
                      setLoginData((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    onFocus={(e) => {
                      if (e.target.value !== loginData.password) {
                        setLoginData((prev) => ({
                          ...prev,
                          password: e.target.value,
                        }));
                      }
                    }}
                    autoFocus
                    required
                    className="w-full h-12 px-4 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all shadow-sm"
                  />
                </div>

                <div className="space-y-3">
                  <button
                    type="submit"
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium rounded-xl transition-all duration-200 flex items-center justify-center shadow-sm"
                    disabled={loginMutation.isPending}
                    data-testid="button-login-submit"
                  >
                    {loginMutation.isPending ? "Signing In..." : "Sign In"}
                  </button>

                  <button
                    type="button"
                    className="w-full text-sm text-slate-500 hover:text-blue-600 transition-colors py-1"
                    onClick={() =>
                      setLocation(
                        `/forgot-password?email=${encodeURIComponent(userEmail)}`,
                      )
                    }
                  >
                    Forgot your password?
                  </button>

                  <button
                    type="button"
                    className="w-full text-sm text-slate-500 hover:text-slate-700 transition-colors py-1"
                    onClick={handleBackToEmail}
                  >
                    Use a different email
                  </button>
                </div>
              </form>
            )}

            {/* Step 3: Registration Form */}
            {authStep === "register" && (
              <div>
                <form
                  onSubmit={handleRegisterSubmit}
                  className="space-y-3"
                  autoComplete="on"
                  id="register-form"
                  name="register-form"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        type="text"
                        placeholder="John"
                        value={registerData.firstName}
                        onChange={(e) =>
                          setRegisterData((prev) => ({
                            ...prev,
                            firstName: e.target.value,
                          }))
                        }
                        required
                        className="h-11 rounded-xl border-slate-200"
                        autoComplete="given-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        type="text"
                        placeholder="Doe"
                        value={registerData.lastName}
                        onChange={(e) =>
                          setRegisterData((prev) => ({
                            ...prev,
                            lastName: e.target.value,
                          }))
                        }
                        required
                        className="h-11 rounded-xl border-slate-200"
                        autoComplete="family-name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="organizationName">Organization Name</Label>
                    <Input
                      id="organizationName"
                      name="organizationName"
                      type="text"
                      placeholder="Your MSP or company name"
                      value={registerData.organizationName}
                      onChange={(e) =>
                        setRegisterData((prev) => ({
                          ...prev,
                          organizationName: e.target.value,
                        }))
                      }
                      required
                      className="h-11 rounded-xl border-slate-200"
                      autoComplete="organization"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="At least 8 characters"
                      value={registerData.password}
                      onChange={(e) =>
                        setRegisterData((prev) => ({
                          ...prev,
                          password: e.target.value,
                        }))
                      }
                      required
                      className="h-11 rounded-xl border-slate-200"
                      autoComplete="new-password"
                      data-lpignore="false"
                      data-keeper-id="password"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="Confirm your password"
                      value={registerData.confirmPassword}
                      onChange={(e) =>
                        setRegisterData((prev) => ({
                          ...prev,
                          confirmPassword: e.target.value,
                        }))
                      }
                      required
                      className="h-11 rounded-xl border-slate-200"
                      autoComplete="new-password"
                      data-lpignore="false"
                      data-keeper-id="confirmPassword"
                    />
                  </div>

                  <div className="space-y-3 pt-1">
                    <button
                      type="submit"
                      className="w-full h-12 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium rounded-xl transition-all duration-200 flex items-center justify-center shadow-sm"
                      disabled={registerMutation.isPending}
                    >
                      {registerMutation.isPending
                        ? "Creating Account..."
                        : "Create Account"}
                    </button>

                    <button
                      type="button"
                      className="w-full text-sm text-slate-500 hover:text-slate-700 transition-colors py-1"
                      onClick={handleBackToEmail}
                    >
                      Use a different email
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Link for new users to access pricing */}
            {authStep === "email" && (
              <p className="mt-6 text-center text-sm text-slate-500">
                Don't have an account?{" "}
                <button
                  type="button"
                  onClick={() => {
                    window.open("https://techalign.app/pricing", "_blank");
                  }}
                  className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
                >
                  Start a free 14-day trial
                </button>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* MFA Verification Dialog */}
      <Dialog open={showMfaVerification} onOpenChange={setShowMfaVerification}>
        <DialogContent className="max-w-sm p-0 overflow-hidden">
          <div className="p-6 pb-4">
            {/* Header with icon */}
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Shield className="h-7 w-7 text-primary" />
              </div>
              <DialogTitle className="text-xl font-semibold mb-1">
                Verify your identity
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                Enter the 6-digit code from your authenticator app
              </DialogDescription>
            </div>

            {!showBackupCodeInput ? (
              <div className="space-y-5">
                {/* Code input with individual digit styling */}
                <div className="flex flex-col items-center relative">
                  <div className="inline-flex justify-center gap-2 pointer-events-none">
                    {[0, 1, 2, 3, 4, 5].map((index) => {
                      const isFilled = !!mfaToken[index];
                      const isActive = index === mfaToken.length;
                      return (
                        <div
                          key={index}
                          className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center text-xl font-semibold font-mono transition-all ${
                            isFilled
                              ? "border-primary bg-primary/5"
                              : isActive
                                ? "border-blue-500 ring-2 ring-blue-500/30 bg-blue-500/5"
                                : "border-border bg-muted/30"
                          }`}
                        >
                          {mfaToken[index] || ""}
                        </div>
                      );
                    })}
                  </div>
                  {/* Transparent input overlay for typing and pasting */}
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={mfaToken}
                    onChange={(e) =>
                      setMfaToken(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        mfaToken.length === 6 &&
                        !mfaVerifyMutation.isPending
                      ) {
                        mfaVerifyMutation.mutate({
                          email: mfaEmail,
                          token: mfaToken,
                          isBackupCode: false,
                        });
                      }
                    }}
                    maxLength={6}
                    autoFocus
                    autoComplete="one-time-code"
                    name="totp"
                    id="totp-code"
                    className="absolute inset-0 w-full h-full opacity-[0.01] cursor-text"
                    aria-label="Enter verification code"
                  />
                </div>

                {/* Account hint */}
                <p className="text-xs text-center text-muted-foreground">
                  Signing in as{" "}
                  <span className="font-medium text-foreground">
                    {mfaEmail}
                  </span>
                </p>

                {/* Verify button */}
                <Button
                  onClick={() =>
                    mfaVerifyMutation.mutate({
                      email: mfaEmail,
                      token: mfaToken,
                    })
                  }
                  disabled={
                    mfaToken.length !== 6 || mfaVerifyMutation.isPending
                  }
                  className="w-full h-11"
                >
                  {mfaVerifyMutation.isPending ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Verifying...
                    </span>
                  ) : (
                    "Continue"
                  )}
                </Button>

                {/* Backup code link */}
                <button
                  type="button"
                  onClick={() => setShowBackupCodeInput(true)}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1.5"
                >
                  <Key className="h-3.5 w-3.5" />
                  Use a backup code instead
                </button>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Backup code input */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Backup Code</Label>
                  <Input
                    value={backupCode}
                    onChange={(e) =>
                      setBackupCode(
                        e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
                      )
                    }
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        backupCode.length === 8 &&
                        !mfaVerifyMutation.isPending
                      ) {
                        mfaVerifyMutation.mutate({
                          email: mfaEmail,
                          token: backupCode,
                          isBackupCode: true,
                        });
                      }
                    }}
                    placeholder="XXXXXXXX"
                    maxLength={8}
                    className="text-center font-mono text-lg tracking-[0.3em] h-12"
                    autoFocus
                  />
                  <p className="text-xs text-muted-foreground text-center">
                    Enter one of your 8-character backup codes
                  </p>
                </div>

                {/* Verify with backup code */}
                <Button
                  onClick={() => {
                    if (backupCode.length === 8) {
                      mfaVerifyMutation.mutate({
                        email: mfaEmail,
                        token: backupCode,
                        isBackupCode: true,
                      });
                    }
                  }}
                  disabled={
                    backupCode.length !== 8 || mfaVerifyMutation.isPending
                  }
                  className="w-full h-11"
                >
                  {mfaVerifyMutation.isPending ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Verifying...
                    </span>
                  ) : (
                    "Verify Backup Code"
                  )}
                </Button>

                {/* Back to authenticator */}
                <button
                  type="button"
                  onClick={() => {
                    setShowBackupCodeInput(false);
                    setBackupCode("");
                  }}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1.5"
                >
                  <Smartphone className="h-3.5 w-3.5" />
                  Use authenticator app instead
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-muted/30 border-t">
            <button
              type="button"
              onClick={() => {
                setShowMfaVerification(false);
                setShowBackupCodeInput(false);
                setMfaToken("");
                setMfaEmail("");
                setBackupCode("");
              }}
              className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel and return to login
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AuthSimpleWithCaptcha() {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || ""}
    >
      <AuthSimple />
    </GoogleReCaptchaProvider>
  );
}
